import React from 'react';
import { Eye, Mail, Pencil, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { formatDate } from '../../../config/appConfig';
import type { EmailAccount, EmailAccountSendIdentity } from '../useData';
import ShareAccountButton from './ShareAccountButton';

type SyncVisual = 'idle' | 'syncing' | 'error' | 'paused';

function resolveSyncVisual(account: EmailAccount): SyncVisual {
  if (!account.syncEnabled) return 'paused';
  if (account.syncStatus === 'syncing') return 'syncing';
  if (account.syncStatus === 'error') return 'error';
  return 'idle';
}

function syncVisualStyles(visual: SyncVisual) {
  switch (visual) {
    case 'syncing':
      return {
        accent: 'border-l-sky-500',
        badge: 'border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300',
        label: 'Syncing',
        dot: 'bg-sky-500 animate-pulse',
      };
    case 'error':
      return {
        accent: 'border-l-rose-500',
        badge: 'border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300',
        label: 'Error',
        dot: 'bg-rose-500',
      };
    case 'paused':
      return {
        accent: 'border-l-slate-400',
        badge: 'border-slate-500/30 bg-slate-500/10 text-slate-700 dark:text-slate-300',
        label: 'Paused',
        dot: 'bg-slate-400',
      };
    default:
      return {
        accent: 'border-l-emerald-500',
        badge: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
        label: 'Idle',
        dot: 'bg-emerald-500',
      };
  }
}

function protocolLabel(account: EmailAccount): string {
  if (account.oauthProvider) {
    const provider = account.oauthProvider.toLowerCase();
    if (provider.includes('google') || provider.includes('gmail')) return 'Google OAuth';
    return 'OAuth';
  }
  return account.incomingProtocol === 'pop3' ? 'POP3' : 'IMAP';
}

function sortIdentities(identities: EmailAccountSendIdentity[]): EmailAccountSendIdentity[] {
  return [...identities].sort((a, b) => {
    if (a.isDefault === b.isDefault) return a.displayOrder - b.displayOrder;
    return a.isDefault ? -1 : 1;
  });
}

interface EmailAccountCardProps {
  account: EmailAccount;
  identities: EmailAccountSendIdentity[];
  detailHref: string;
  editHref: string;
  onDelete: () => void;
  deleting: boolean;
  onShared: () => void;
}

const EmailAccountCard: React.FC<EmailAccountCardProps> = ({
  account,
  identities,
  detailHref,
  editHref,
  onDelete,
  deleting,
  onShared,
}) => {
  const { colors } = useTheme();
  const syncVisual = syncVisualStyles(resolveSyncVisual(account));
  const sortedIdentities = sortIdentities(identities);
  const emailCount = account._count?.emails ?? 0;
  const identityCount = account._count?.sendIdentities ?? sortedIdentities.length;
  const shareCount = account._count?.shares ?? 0;

  return (
    <article
      className={cn(
        'overflow-hidden rounded-xl border border-l-4 transition-shadow hover:shadow-md',
        colors.border,
        colors.surface,
        syncVisual.accent,
      )}
    >
      <div className="flex flex-col gap-4 p-4 sm:p-5 lg:flex-row lg:items-start lg:justify-between">
        <Link to={detailHref} className="group flex min-w-0 flex-1 items-start gap-4">
          <div
            className={cn(
              'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border',
              colors.border,
              colors.surfaceHover,
              'text-indigo-600 dark:text-indigo-400',
            )}
          >
            <Mail className="h-5 w-5" aria-hidden />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className={cn('truncate text-base font-semibold group-hover:underline', colors.text)}>
                {account.emailAddress}
              </h2>
              <span
                className={cn(
                  'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide',
                  colors.surfaceHover,
                  colors.textSecondary,
                )}
              >
                {protocolLabel(account)}
              </span>
              <span
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium',
                  syncVisual.badge,
                )}
              >
                <span className={cn('h-1.5 w-1.5 rounded-full', syncVisual.dot)} aria-hidden />
                {syncVisual.label}
              </span>
            </div>

            {account.displayName ? (
              <p className={cn('mt-0.5 truncate text-sm', colors.textSecondary)}>{account.displayName}</p>
            ) : null}

            {account.syncStatus === 'error' && account.syncError ? (
              <p className={cn('mt-2 line-clamp-2 text-xs', 'text-rose-600 dark:text-rose-400')} title={account.syncError}>
                {account.syncError}
              </p>
            ) : null}
          </div>
        </Link>

        <div className="flex shrink-0 items-center gap-1 self-end lg:self-start">
          <Link
            to={detailHref}
            title="View account"
            className={cn(
              'inline-flex h-9 w-9 items-center justify-center rounded-lg transition-colors',
              colors.text,
              colors.interactiveHover,
            )}
          >
            <Eye className="h-4 w-4" />
          </Link>
          <Link
            to={editHref}
            title="Edit account"
            className={cn(
              'inline-flex h-9 w-9 items-center justify-center rounded-lg transition-colors',
              colors.text,
              colors.interactiveHover,
            )}
          >
            <Pencil className="h-4 w-4" />
          </Link>
          <ShareAccountButton account={account} onShared={onShared} iconOnly />
          <button
            type="button"
            title="Delete account"
            disabled={deleting}
            onClick={onDelete}
            className={cn(
              'inline-flex h-9 w-9 items-center justify-center rounded-lg transition-colors',
              'text-rose-600 hover:bg-rose-50 hover:text-rose-700 dark:hover:bg-rose-950/30',
              deleting && 'cursor-not-allowed opacity-50',
            )}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className={cn('grid grid-cols-2 gap-px border-t sm:grid-cols-4', colors.border, colors.surfaceHover)}>
        <StatCell label="Emails" value={emailCount.toLocaleString()} />
        <StatCell
          label="Identities"
          value={String(identityCount)}
          tone={identityCount === 0 ? 'warning' : 'default'}
        />
        <StatCell
          label="Sharing"
          value={shareCount === 0 ? 'Private' : `${shareCount} user${shareCount === 1 ? '' : 's'}`}
          tone={shareCount > 0 ? 'success' : 'muted'}
        />
        <StatCell label="Last sync" value={account.lastSyncAt ? formatDate(account.lastSyncAt) : 'Never'} />
      </div>

      <div className={cn('border-t px-4 py-3 sm:px-5', colors.border)}>
        <p className={cn('mb-2 text-[11px] font-semibold uppercase tracking-wide', colors.textMuted)}>Send as</p>
        <div className="flex flex-wrap gap-1.5">
          {sortedIdentities.length === 0 ? (
            <IdentityPill email={account.emailAddress} defaultIdentity />
          ) : (
            <>
              {sortedIdentities.slice(0, 4).map((identity) => (
                <IdentityPill
                  key={identity.id}
                  email={identity.emailAddress || account.emailAddress}
                  defaultIdentity={identity.isDefault}
                />
              ))}
              {sortedIdentities.length > 4 ? (
                <span
                  className={cn(
                    'inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium',
                    colors.border,
                    colors.textMuted,
                  )}
                >
                  +{sortedIdentities.length - 4} more
                </span>
              ) : null}
            </>
          )}
        </div>
      </div>
    </article>
  );
};

function StatCell({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: string;
  tone?: 'default' | 'warning' | 'success' | 'muted';
}) {
  const { colors } = useTheme();
  const valueClass =
    tone === 'warning'
      ? 'text-amber-700 dark:text-amber-300'
      : tone === 'success'
        ? 'text-emerald-700 dark:text-emerald-300'
        : tone === 'muted'
          ? colors.textMuted
          : colors.text;

  return (
    <div className={cn('px-4 py-3', colors.surface)}>
      <p className={cn('text-[11px] font-semibold uppercase tracking-wide', colors.textMuted)}>{label}</p>
      <p className={cn('mt-1 text-sm font-semibold tabular-nums', valueClass)}>{value}</p>
    </div>
  );
}

function IdentityPill({ email, defaultIdentity }: { email: string; defaultIdentity?: boolean }) {
  const { colors } = useTheme();
  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center truncate rounded-full border px-2.5 py-0.5 text-[11px] font-medium',
        defaultIdentity
          ? 'border-indigo-500/30 bg-indigo-500/10 text-indigo-700 dark:text-indigo-300'
          : cn(colors.border, colors.textSecondary),
      )}
      title={email}
    >
      {email}
      {defaultIdentity ? <span className="ml-1 opacity-80">★</span> : null}
    </span>
  );
}

export default EmailAccountCard;
